import {
  GenericTypeahead,
  updateTypeaheadDisabledState,
} from "../components/typeahead";
import { Option } from "../utils/validation";
import {
  ATTACHMENT_KEY_PREFIX,
  AttachmentTypeOption,
} from "./attachment-manager";
import { createFromTemplate } from "./sync-select-builder";
import { HPD_RESTRICTED_ATTACHMENT_TYPES } from "./attachment-table";

// Centralized dropdown management class
export class DropdownManager {
  private attachmentTypes: AttachmentTypeOption[];
  private currentProgramAreaId: number | null;
  private templateId: string;
  private typeaheadContainer: GenericTypeahead;
  private attachmentTypeSelections: Map<string, number>;
  private isHpdStaff: boolean;

  constructor(
    attachmentTypes: AttachmentTypeOption[],
    currentProgramAreaId: number | null,
    templateId: string,
    typeaheadContainer: GenericTypeahead,
    attachmentTypeSelections: Map<string, number>,
    isHpdStaff: boolean,
  ) {
    this.attachmentTypes = attachmentTypes;
    this.currentProgramAreaId = currentProgramAreaId;
    this.templateId = templateId;
    this.typeaheadContainer = typeaheadContainer;
    this.attachmentTypeSelections = attachmentTypeSelections;
    this.isHpdStaff = isHpdStaff;
  }

  public updateProgramAreaId(programAreaId: number | null): void {
    this.currentProgramAreaId = programAreaId;
  }

  public getFilteredOptions(): Option[] {
    return this.attachmentTypes
      .filter((option) => {
        // Filter by program area only
        return (
          this.currentProgramAreaId == null ||
          option.ProjectProgramAreaId == this.currentProgramAreaId
        );
      })
      .map((at) => ({
        value: at.Id.toString(),
        display: at.Name,
      }));
  }

  public createDropdown(
    inputId: string,
    fileIndex: number | null,
    existingFileId?: string,
    currentValue?: string,
  ): HTMLElement {
    const filteredOptions = this.getFilteredOptions();
    const isProgramAreaDisabled = this.currentProgramAreaId == null;
    const isHpdRestricted = this.isCurrentValueHpdRestricted(currentValue);
    const isDisabled =
      isProgramAreaDisabled ||
      (existingFileId != null && !this.isHpdStaff && isHpdRestricted);

    const dropdown = createFromTemplate(
      this.templateId,
      inputId,
      "Attachment Type",
      "displayProperty",
      "value",
      currentValue,
      true,
      filteredOptions,
    );

    // Add change handler for the hidden input
    const hiddenInput = dropdown.querySelector(
      'input[type="hidden"]',
    ) as HTMLInputElement;
    const visibleInput = dropdown.querySelector(
      'input[type="text"]',
    ) as HTMLInputElement;

    if (hiddenInput) {
      hiddenInput.setAttribute("data-valid-sync-select", "true");
      // Set initial value if provided
      if (currentValue) {
        hiddenInput.value = currentValue;
        // Also update the internal tracking
        this.updateSelection(fileIndex, existingFileId, parseInt(currentValue));
      }
    }

    if (hiddenInput) {
      hiddenInput.addEventListener("change", (e) => {
        this.handleSelectionChange(
          e.target as HTMLInputElement,
          fileIndex,
          existingFileId,
        );
      });
    }

    // Set initial disabled state and tooltip
    const tooltip = isProgramAreaDisabled
      ? "Select a Program Area"
      : !this.isHpdStaff && isHpdRestricted && existingFileId != null
        ? "HPD staff only - cannot modify"
        : "";
    updateTypeaheadDisabledState(dropdown, isDisabled, tooltip);

    return dropdown;
  }

  public updateDropdownOptions(
    dropdown: HTMLElement,
    isExisting: boolean,
  ): void {
    const hiddenInput = dropdown.querySelector(
      'input[type="hidden"]',
    ) as HTMLInputElement;
    const currentValue = hiddenInput?.value || "";
    const options = this.getFilteredOptions();

    const select = dropdown as HTMLSelectElement;

    // Clear and rebuild options
    select.innerHTML = '<option value="">Select attachment type...</option>';

    options.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option.value;
      optionElement.textContent = option.display;
      if (option.value === currentValue) {
        optionElement.selected = true;
      }
      select.appendChild(optionElement);
    });

    // Clear selection if it's no longer valid
    if (currentValue && !options.find((opt) => opt.value === currentValue)) {
      select.value = "";
      if (hiddenInput) hiddenInput.value = "";
    }

    const isProgramAreaDisabled = this.currentProgramAreaId == null;
    const isHpdRestricted = this.isCurrentValueHpdRestricted(currentValue);
    const isDisabled =
      isProgramAreaDisabled ||
      (isExisting && !this.isHpdStaff && isHpdRestricted);

    const tooltip = isProgramAreaDisabled
      ? "Select a Program Area"
      : !this.isHpdStaff && isHpdRestricted && isExisting
        ? "HPD staff only - cannot modify"
        : "";
    updateTypeaheadDisabledState(dropdown, isDisabled, tooltip);
  }

  // TODO: move into attachment table manager
  private updateSelection(
    fileIndex: number | null,
    existingFileId: string | undefined,
    selectedValue: number,
  ): void {
    if (fileIndex !== null) {
      if (selectedValue) {
        this.attachmentTypeSelections.set(
          `${ATTACHMENT_KEY_PREFIX.NEW}${fileIndex}`,
          selectedValue,
        );
      } else {
        this.attachmentTypeSelections.delete(
          `${ATTACHMENT_KEY_PREFIX.NEW}${fileIndex}`,
        );
      }
    } else if (existingFileId) {
      if (selectedValue) {
        this.attachmentTypeSelections.set(
          `${ATTACHMENT_KEY_PREFIX.EXISTING}${existingFileId}`,
          selectedValue,
        );
      } else {
        this.attachmentTypeSelections.delete(
          `${ATTACHMENT_KEY_PREFIX.EXISTING}${existingFileId}`,
        );
      }
    }
  }

  private handleSelectionChange(
    element: HTMLInputElement | HTMLSelectElement,
    fileIndex: number | null,
    existingFileId?: string,
  ): void {
    const selectedValue = parseInt(element.value);
    this.updateSelection(fileIndex, existingFileId, selectedValue);
  }

  public getAttachmentTypeName(attachmentTypeId: number): string | undefined {
    const attachmentType = this.attachmentTypes.find(
      (at) => at.Id === attachmentTypeId,
    );
    return attachmentType?.Name;
  }

  private isCurrentValueHpdRestricted(currentValue?: string): boolean {
    if (!currentValue) return false;
    const attachmentType = this.attachmentTypes.find(
      (at) => at.Id.toString() === currentValue,
    );
    return attachmentType
      ? HPD_RESTRICTED_ATTACHMENT_TYPES.includes(attachmentType.Name)
      : false;
  }
}
